import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import i18nInstance from '@/i18n/config';
import { Search, Check, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { apiGet, apiPost } from '@/lib/api';
import { showToast } from './Toast';
import { useAuthStore } from '@/stores';

// Force re-render on language change
const useForceUpdate = () => {
  const [, setForceUpdate] = useState(0);
  useEffect(() => {
    const onLanguageChanged = () => setForceUpdate(n => n + 1);
    i18nInstance.on('languageChanged', onLanguageChanged);
    return () => i18nInstance.off('languageChanged', onLanguageChanged);
  }, []);
};

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  isDeveloper?: boolean;
  isProjectMember?: boolean;
}

interface BatchShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  conversationIds: string[];
  conversationTitles: string[];
  projectId?: string | null;
  onSuccess?: () => void;
}

export default function BatchShareModal({
  isOpen,
  onClose,
  conversationIds,
  conversationTitles,
  projectId,
  onSuccess,
}: BatchShareModalProps) {
  const { t } = useTranslation();
  useForceUpdate();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [sharedDescription, setSharedDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const hasLoadedUsersRef = useRef(false);
  // 新增：项目成员的 id 集合
  const [projectMemberIds, setProjectMemberIds] = useState<Set<string>>(
    new Set()
  );

  // Get current user info
  const { userInfo } = useAuthStore();
  const currentUserId = userInfo?.id;

  // 新增：获取项目成员列表
  const loadProjectMembers = useCallback(async () => {
    if (!projectId) {
      setProjectMemberIds(new Set());
      return;
    }

    try {
      const url = `/api/projects/${projectId}/users`;

      const response = await apiGet<any>(url);

      const data = response?.data || response || {};
      const members = data.users || data || [];

      // 创建项目成员 id 的集合（统一转换为字符串）
      const memberIds = new Set<string>(
        members
          .filter((member: any) => member.user && member.user.id)
          .map((member: any) => String(member.user.id))
      );

      console.log(
        '[BatchShareModal] Project member ids:',
        Array.from(memberIds)
      );
      setProjectMemberIds(memberIds);
    } catch (error) {
      console.error('[BatchShareModal] Error fetching project members:', error);
      setProjectMemberIds(new Set());
    }
  }, [projectId]);

  // Fetch users from API
  const loadUsersFromAPI = useCallback(async () => {
    if (hasLoadedUsersRef.current) {
      return;
    }
    try {
      setIsLoading(true);
      const url = '/api/shared-users';
      const response = await apiGet<any>(url);
      const data = response?.data || response || {};
      if (data) {
        // 排除当前登录用户
        const allUsers = data.users || [];
        const filteredOutCurrentUser = currentUserId
          ? allUsers.filter((user: User) => {
              const userId = String(user.id);
              const currentId = String(currentUserId);
              const shouldKeep = userId !== currentId;
              return shouldKeep;
            })
          : allUsers;

        // 处理用户数据
        const processedUsers = filteredOutCurrentUser.map((user: any) => ({
          ...user,
          isDeveloper: user.isDeveloper || false,
        }));

        setUsers(processedUsers);
        setFilteredUsers(processedUsers);
        hasLoadedUsersRef.current = true;
      } else {
        showToast(
          t('errors.failedToFetchUserList') || '获取用户列表失败',
          'error'
        );
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      showToast(
        t('errors.failedToFetchUserList') || '获取用户列表失败',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  }, [currentUserId, t]);

  // Filter users based on search query
  const filterUsers = useCallback(
    (search: string) => {
      const trimmedSearch = search.trim().toLowerCase();
      if (trimmedSearch) {
        setFilteredUsers(
          users.filter(
            user =>
              user.name.toLowerCase().includes(trimmedSearch) ||
              user.email.toLowerCase().includes(trimmedSearch)
          )
        );
      } else {
        setFilteredUsers(users);
      }
    },
    [users]
  );

  // Handle search with debounce
  useEffect(() => {
    if (!hasLoadedUsersRef.current) {
      return;
    }
    const debounceTimer = setTimeout(() => {
      filterUsers(searchQuery);
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery, filterUsers]);

  // Initial load when modal opens
  useEffect(() => {
    if (isOpen) {
      // 重置加载状态，允许重新加载
      hasLoadedUsersRef.current = false;
      setSelectedUsers(new Set());
      setSearchQuery('');
      setSharedDescription('');

      // 确保在获取 userInfo 后再加载
      if (currentUserId) {
        loadUsersFromAPI();
        loadProjectMembers();
      } else {
        // 如果 currentUserId 还没有，稍后再试
        setTimeout(() => {
          hasLoadedUsersRef.current = false;
          loadUsersFromAPI();
          loadProjectMembers();
        }, 100);
      }
      // Focus search input when modal opens
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentUserId, loadProjectMembers]);

  // Handle user selection
  const toggleUserSelection = useCallback((userId: string) => {
    setSelectedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  }, []);

  // Handle select all
  const handleSelectAll = useCallback(() => {
    const isAllSelected = filteredUsers.every(user =>
      selectedUsers.has(user.id)
    );
    if (isAllSelected) {
      // Deselect all visible users
      setSelectedUsers(prev => {
        const newSet = new Set(prev);
        filteredUsers.forEach(user => newSet.delete(user.id));
        return newSet;
      });
    } else {
      // Select all visible users
      setSelectedUsers(prev => {
        const newSet = new Set(prev);
        filteredUsers.forEach(user => newSet.add(user.id));
        return newSet;
      });
    }
  }, [filteredUsers, selectedUsers]);

  // Handle select project members
  const handleSelectProjectMembers = useCallback(() => {
    if (!projectId || projectMemberIds.size === 0) return;

    // 通过 id 匹配找出项目成员（统一转换为字符串比较）
    const projectMembers = filteredUsers.filter(user =>
      projectMemberIds.has(String(user.id))
    );

    const isAllProjectMembersSelected = projectMembers.every(user =>
      selectedUsers.has(user.id)
    );

    if (isAllProjectMembersSelected) {
      // Deselect all project members
      setSelectedUsers(prev => {
        const newSet = new Set(prev);
        projectMembers.forEach(user => newSet.delete(user.id));
        return newSet;
      });
    } else {
      // Select all project members
      setSelectedUsers(prev => {
        const newSet = new Set(prev);
        projectMembers.forEach(user => newSet.add(user.id));
        return newSet;
      });
    }
  }, [filteredUsers, selectedUsers, projectId, projectMemberIds]);

  // Handle select developers
  const handleSelectDevelopers = useCallback(() => {
    const developers = filteredUsers.filter(user => user.isDeveloper);
    const isAllDevelopersSelected = developers.every(user =>
      selectedUsers.has(user.id)
    );

    if (isAllDevelopersSelected) {
      // Deselect all developers
      setSelectedUsers(prev => {
        const newSet = new Set(prev);
        developers.forEach(user => newSet.delete(user.id));
        return newSet;
      });
    } else {
      // Select all developers
      setSelectedUsers(prev => {
        const newSet = new Set(prev);
        developers.forEach(user => newSet.add(user.id));
        return newSet;
      });
    }
  }, [filteredUsers, selectedUsers]);

  // Handle batch share
  const handleBatchShare = async () => {
    if (selectedUsers.size === 0) {
      showToast(t('share.selectUsersToShare') || '请选择要分享的用户', 'error');
      return;
    }

    if (conversationIds.length === 0) {
      showToast(
        t('share.noConversationsToShare') || '没有可分享的会话',
        'error'
      );
      return;
    }

    try {
      setIsSharing(true);
      const userIds = Array.from(selectedUsers).map(id => Number(id));

      const res = await apiPost('/api/ai/conversations/batch/share', {
        userIds,
        sharedDescription,
        conversationIds,
      });

      if (res.code === 200 || res.success) {
        showToast(
          t('toast.batchShareSuccess') ||
            `成功分享 ${conversationIds.length} 个会话`,
          'success'
        );
        if (onSuccess) {
          onSuccess();
        } else {
          onClose();
        }
      } else {
        throw new Error(res.message || 'Batch share failed');
      }
    } catch (error) {
      console.error('Error batch sharing conversations:', error);
      showToast(t('toast.batchShareFailed') || '批量分享失败', 'error');
    } finally {
      setIsSharing(false);
    }
  };

  // Get user avatar initials
  const getAvatarInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  // Generate avatar background color based on name
  const getAvatarColor = (name: string) => {
    const colors = [
      'bg-red-400',
      'bg-orange-400',
      'bg-yellow-400',
      'bg-green-400',
      'bg-blue-400',
      'bg-indigo-400',
      'bg-purple-400',
      'bg-pink-400',
      'bg-teal-400',
      'bg-cyan-400',
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  if (!isOpen) return null;

  // 计算"全部"按钮的三态
  const allSelectedCount = filteredUsers.filter(user =>
    selectedUsers.has(user.id)
  ).length;
  const isAllSelected =
    filteredUsers.length > 0 && allSelectedCount === filteredUsers.length;
  const isSomeSelected =
    allSelectedCount > 0 && allSelectedCount < filteredUsers.length;

  // 计算"项目成员"按钮的三态
  const projectMembers = filteredUsers.filter(user =>
    projectMemberIds.has(String(user.id))
  );
  const pmSelectedCount = projectMembers.filter(user =>
    selectedUsers.has(user.id)
  ).length;
  const isAllProjectMembersSelected =
    projectMembers.length > 0 && pmSelectedCount === projectMembers.length;
  const isSomeProjectMembersSelected =
    pmSelectedCount > 0 && pmSelectedCount < projectMembers.length;

  // 计算"开发者"按钮的三态
  const developers = filteredUsers.filter(user => user.isDeveloper);
  const devSelectedCount = developers.filter(user =>
    selectedUsers.has(user.id)
  ).length;
  const isAllDevelopersSelected =
    developers.length > 0 && devSelectedCount === developers.length;
  const isSomeDevelopersSelected =
    devSelectedCount > 0 && devSelectedCount < developers.length;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle>
            {t('share.batchShareConversations') || '批量分享会话'}
          </DialogTitle>
          <div className="mt-2">
            <p className="text-sm text-gray-600">
              {t('share.batchShareDescription', {
                count: conversationIds.length,
              }) || `将 ${conversationIds.length} 个会话分享给选定的用户`}
            </p>
            <div className="mt-2 max-h-20 overflow-y-auto">
              <div className="text-xs text-gray-500">
                {conversationTitles.slice(0, 3).map((title, index) => (
                  <div key={index} className="truncate">
                    • {title}
                  </div>
                ))}
                {conversationTitles.length > 3 && (
                  <div className="text-xs text-gray-400 italic">
                    {t('share.andMore', {
                      count: conversationTitles.length - 3,
                    }) || `还有 ${conversationTitles.length - 3} 个会话...`}
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogHeader>

        {/* Search */}
        <div className="px-6 py-2 border-b">
          <div className="relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            />
            <input
              ref={searchInputRef}
              type="text"
              placeholder={t('form.searchMembers') || '搜索成员'}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-1 mb-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Select Options */}
        {filteredUsers.length > 0 && (
          <div className="px-6 py-2 border-b">
            <div className="flex gap-2">
              {/* All Button */}
              <button
                onClick={handleSelectAll}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors hover:bg-gray-100 border border-gray-300"
              >
                <div
                  className={cn(
                    'w-4 h-4 border-2 rounded flex items-center justify-center transition-colors',
                    isAllSelected || isSomeSelected
                      ? 'border-blue-600 bg-blue-600'
                      : 'border-gray-300'
                  )}
                >
                  {isAllSelected && <Check size={10} className="text-white" />}
                  {isSomeSelected && <Minus size={10} className="text-white" />}
                </div>
                {t('share.all') || '全部'}
              </button>

              {/* Project Members Button - only shown when launched from project detail page */}
              {projectId && (
                <button
                  onClick={handleSelectProjectMembers}
                  disabled={projectMemberIds.size === 0}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors border border-gray-300 ${
                    projectMemberIds.size === 0
                      ? 'opacity-50 cursor-not-allowed bg-gray-50'
                      : 'hover:bg-gray-100'
                  }`}
                >
                  <div
                    className={cn(
                      'w-4 h-4 border-2 rounded flex items-center justify-center transition-colors',
                      isAllProjectMembersSelected ||
                        isSomeProjectMembersSelected
                        ? 'border-blue-600 bg-blue-600'
                        : 'border-gray-300'
                    )}
                  >
                    {isAllProjectMembersSelected && (
                      <Check size={10} className="text-white" />
                    )}
                    {isSomeProjectMembersSelected && (
                      <Minus size={10} className="text-white" />
                    )}
                  </div>
                  {t('share.projectMembers') || '项目成员'}
                </button>
              )}

              {/* Developers Button - always shown */}
              <button
                onClick={handleSelectDevelopers}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors hover:bg-gray-100 border border-gray-300"
              >
                <div
                  className={cn(
                    'w-4 h-4 border-2 rounded flex items-center justify-center transition-colors',
                    isAllDevelopersSelected || isSomeDevelopersSelected
                      ? 'border-blue-600 bg-blue-600'
                      : 'border-gray-300'
                  )}
                >
                  {isAllDevelopersSelected && (
                    <Check size={10} className="text-white" />
                  )}
                  {isSomeDevelopersSelected && (
                    <Minus size={10} className="text-white" />
                  )}
                </div>
                {t('share.developers') || '开发者'}
              </button>
            </div>
          </div>
        )}

        {/* Users list */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="px-6 py-3 text-center text-gray-500">
              {t('form.loadingUsers') || '加载用户中...'}
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="px-6 py-3 text-center text-gray-500">
              {searchQuery
                ? t('form.noMatchingUsers') || '没有匹配的用户'
                : t('form.noUsersAvailable') || '没有可用的用户'}
            </div>
          ) : (
            <div className="px-6 py-2 space-y-2">
              {filteredUsers.map(user => (
                <button
                  key={user.id}
                  onClick={() => toggleUserSelection(user.id)}
                  className="flex items-center gap-3 pb-2 border-b w-full text-left hover:bg-gray-50 rounded-md transition-colors"
                >
                  {/* Avatar */}
                  <div
                    className={cn(
                      'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium',
                      getAvatarColor(user.name)
                    )}
                  >
                    {user.avatar ? (
                      <img
                        src={user.avatar}
                        alt={user.name}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      getAvatarInitials(user.name)
                    )}
                  </div>
                  {/* User info */}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {user.name}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {user.email}
                    </div>
                  </div>
                  {/* Checkbox */}
                  <div
                    className={cn(
                      'w-4 h-4 border-2 rounded flex items-center justify-center transition-colors',
                      selectedUsers.has(user.id)
                        ? 'border-blue-600 bg-blue-600'
                        : 'border-gray-300'
                    )}
                  >
                    {selectedUsers.has(user.id) && (
                      <Check size={12} className="text-white" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Description */}
        <div className="px-6 py-3 border-b">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('form.description') || '描述'}
          </label>
          <textarea
            value={sharedDescription}
            onChange={e => setSharedDescription(e.target.value)}
            placeholder={t('form.addDescription') || '添加描述（可选）'}
            rows={2}
            disabled={isSharing}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
          />
        </div>

        {/* Footer */}
        <DialogFooter className="flex-row justify-between items-center px-6 pb-6">
          <div className="text-sm text-gray-600">
            {selectedUsers.size === 0
              ? t('share.noUsersSelected') || '请选择要分享的用户'
              : selectedUsers.size === 1
                ? t('share.userSelected') || '已选择 1 个用户'
                : t('share.usersSelected', { count: selectedUsers.size }) ||
                  `已选择 ${selectedUsers.size} 个用户`}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={isSharing}>
              {t('common.cancel') || '取消'}
            </Button>
            <Button
              onClick={handleBatchShare}
              disabled={isSharing || selectedUsers.size === 0}
            >
              {isSharing
                ? t('share.sharing') || '分享中...'
                : t('share.batchShare') || '批量分享'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
